import * as THREE from "three";
import { SkinSlots } from "./skins/kit/slots";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { MeshoptDecoder } from "three/examples/jsm/libs/meshopt_decoder.module.js";
import { KTX2Loader } from "three/examples/jsm/loaders/KTX2Loader.js";
import { HqMaterials } from "./hqMaterials";
import { CpuRelease } from "./cpuRelease";
import { HqAnchors } from "./hqAnchors";
import { RigidRig } from "./rigidRig";
import { NO_SHADOW_PARTS, bakeRestPose, disposeTree, fetchModelList, loadWithDeadline, poolWork, resolveIn, shadowCaster, tintTree } from "./assetTrees";

/**
 * Loads the HQ models (art/hq) per screen. There is no stand-in kit: a model that did not ship, failed to load or
 * was not in the screen's asset set is simply not drawn until it is in (the Kenney placeholder kit was removed on
 * 2026-10-14 at Kevin's request; a level start offers RETRY for failed models, levelLoading.ts).
 */
export class AssetLibrary {
  private readonly loader = new GLTFLoader();
  private readonly models = new Map<string, THREE.Object3D>();
  readonly gradient: THREE.DataTexture;
  ready = false;

  /**
   * Optional inlined asset store for single-file builds (e.g. a hosted preview page): maps a path suffix such as
   * `hq/tower_cannon_t1.glb` or `hq/manifest.json` to a `data:` URL. Every loader
   * request whose path ends with a key is served from it; everything else fetches normally.
   */
  static inlined: Readonly<Record<string, string>> | null = null;

  /**
   * HQ art (ART_STATUS.md): baked, meshopt-packed GLBs from the art session, served from `art/hq/` and listed in
   * `art/hq/manifest.json` (`{"models": ["tower_cannon_t1", ...]}`). Registered as `hq:<name>`; a view whose model is
   * not in memory draws nothing for it. HQ models are authored 1×1 cell
   * with the top of the ground at 0, so they are not rescaled.
   */
  static readonly HqBase = "./hq/";
  private readonly hqScale = new Map<string, number>();
  hqCount = 0;

  constructor() {
    this.loader.manager.setURLModifier((url) => AssetLibrary.resolveInlined(url));
    this.loader.setMeshoptDecoder(MeshoptDecoder);
    RigidRig.installSkinning();
    // 3-step ramp: shadow, mid, lit. Gives every asset the same cel look.
    const data = new Uint8Array([70, 70, 70, 255, 170, 170, 170, 255, 255, 255, 255, 255]);
    this.gradient = new THREE.DataTexture(data, 3, 1, THREE.RGBAFormat);
    this.gradient.minFilter = this.gradient.magFilter = THREE.NearestFilter;
    this.gradient.needsUpdate = true;
  }

  static resolveInlined(url: string): string { return resolveIn(AssetLibrary.inlined, url); }

  /**
   * `pick` chooses which shipped HQ models to load now (render/assetNeeds.ts: the hub's set at boot); the rest load
   * per screen through `ensure`. Without it every shipped model loads, as before. With no manifest (or nothing that
   * loads) the library stays not `ready` and the views draw their plain primitives (views/common.ts `lib`).
   */
  async load(onProgress?: (done: number, total: number) => void, pick?: (shipped: ReadonlySet<string>) => ReadonlySet<string>): Promise<void> {
    await this.loadHq(onProgress, pick);
    this.ready = this.models.size > 0;
  }

  /** Every model `art/hq/manifest.json` lists: what `ensure` may load. */
  private readonly shipped = new Set<string>();
  /** Models with a KTX2 twin in `hq/ktx2/` (its own manifest.json), when this device takes compressed textures. */
  private readonly ktx2Models = new Set<string>();
  private readonly inflight = new Map<string, Promise<void>>();
  /** Set once boot has loaded its set: from then on, asking `hq()` about a shipped model that is not loaded fetches it. */
  private lazy = false;
  private readonly missed = new Set<string>();
  /** Models whose last load failed: `hq()` does not start them again in the background (only `ensure`, i.e. RETRY). */
  private readonly failed = new Set<string>();

  /** True when the manifest ships this model, loaded or not ("is the HQ set present" checks: surfaceY, roadY). */
  ships(name: string): boolean { return this.shipped.has(name) || this.models.has(`hq:${name}`); }
  get shippedNames(): ReadonlySet<string> { return this.shipped; }
  /** HQ model names currently in memory. */
  loadedNames(): string[] { return [...this.models.keys()].filter((k) => k.startsWith("hq:")).map((k) => k.slice(3)); }

  /**
   * KTX2 (ART_WOW_PASS Phase 3 §9): GPU-compressed textures, transcoded by Basis in a worker to ASTC / ETC2 on
   * iPhone, BC7 on desktop — about −85 % texture memory against the WebP GLBs, which decode to RGBA8. Only used when
   * the GPU takes one of those formats (otherwise Basis would fall back to RGBA8 and save nothing) and the page is not
   * a single-file build. `?webp` forces the WebP set, for A/B captures. Call before `load`.
   */
  static readonly Ktx2Base = "./hq/ktx2/";
  static readonly TranscoderPath = "./basis/";
  static readonly CompressedFormats = ["WEBGL_compressed_texture_astc", "WEBGL_compressed_texture_etc", "EXT_texture_compression_bptc", "WEBGL_compressed_texture_s3tc"];
  private ktx2: KTX2Loader | null = null;
  /** The KTX2 loader once `enableKtx2` has turned it on, shared with the terrain maps (groundMesh.ts); else null. */
  static compressed: KTX2Loader | null = null;
  enableKtx2(renderer: THREE.WebGLRenderer): boolean {
    if (AssetLibrary.inlined !== null) return false;
    if (typeof location !== "undefined" && new URLSearchParams(location.search).has("webp")) return false;
    if (!AssetLibrary.CompressedFormats.some((e) => renderer.extensions.has(e))) return false;
    this.ktx2 = new KTX2Loader(this.loader.manager).setTranscoderPath(AssetLibrary.TranscoderPath).setWorkerLimit(2).detectSupport(renderer);
    AssetLibrary.compressed = this.ktx2;
    AssetLibrary.watchTranscoder(this.ktx2);
    this.loader.setKTX2Loader(this.ktx2);
    return true;
  }
  get usesKtx2(): boolean { return this.ktx2 !== null && this.ktx2Models.size > 0; }

  /** Reads the manifests (and anchors), then loads `pick(shipped)` — or everything. A missing manifest means no models. */
  private async loadHq(onProgress?: (done: number, total: number) => void, pick?: (shipped: ReadonlySet<string>) => ReadonlySet<string>): Promise<void> {
    this.anchors = await HqAnchors.load(AssetLibrary.resolveInlined(`${AssetLibrary.HqBase}anchors.json`));
    const list = (url: string): Promise<string[]> => fetchModelList(AssetLibrary.resolveInlined(url));
    const [names, ktx] = await Promise.all([list(`${AssetLibrary.HqBase}manifest.json`), this.ktx2 ? list(`${AssetLibrary.Ktx2Base}manifest.json`) : Promise.resolve([])]);
    for (const n of names) this.shipped.add(n);
    for (const n of ktx) if (this.shipped.has(n)) this.ktx2Models.add(n);
    await this.ensure(pick ? pick(this.shipped) : this.shipped, onProgress);
    this.lazy = true;
  }

  /**
   * Loads whichever of `names` ship and are not in memory yet; resolves when all of them are in (or failed), with the
   * names that failed — a level start offers RETRY for those (levelLoading.ts); a screen built without them draws
   * nothing where they would stand.
   */
  async ensure(names: Iterable<string>, onProgress?: (done: number, total: number) => void): Promise<string[]> {
    const todo = [...new Set(names)].filter((n) => this.shipped.has(n) && !this.models.has(`hq:${n}`));
    let done = 0;
    await AssetLibrary.pool(todo, async (name) => {
      let p = this.inflight.get(name);
      if (!p) { p = this.loadHqModel(name).finally(() => this.inflight.delete(name)); this.inflight.set(name, p); }
      await p;
      onProgress?.(++done, todo.length);
    });
    return todo.filter((n) => !this.models.has(`hq:${n}`));
  }

  private async loadHqModel(name: string): Promise<void> {
    try {
      const m = await this.prepareHq(name);
      // its textures' mip chains go once the main canvas has them (cpuRelease.ts), unless another WebGL context draws
      // this model too and uploads it again from them
      if (!AssetLibrary.SharedWithOtherContexts.test(name)) CpuRelease.textures(m.root);
      const key = `hq:${name}`;
      this.models.set(key, m.root);
      this.hqScale.set(key, 1);
      if (m.clips.length > 0) this.clips.set(key, m.clips);
      if (m.rigged) this.rigged.add(key);
      this.hqCount++;
      this.failed.delete(name);
    } catch (err) {
      this.failed.add(name);
      console.warn(`hq asset ${name} failed to load`, err);
    }
  }

  /** Loads a shipped model and makes it the library's kind of model (materials, rest pose, rigid rig, skin slots). */
  private async prepareHq(name: string): Promise<HqCopy> {
    let gltf: { scene: THREE.Group; animations: THREE.AnimationClip[] } | null = null;
    if (this.ktx2 && this.ktx2Models.has(name)) {
      try { gltf = await this.loadGlb(`${AssetLibrary.Ktx2Base}${name}.glb`); }
      catch (err) { console.warn(`hq asset ${name}: KTX2 failed, using WebP`, err); }
    }
    gltf ??= await this.loadGlb(`${AssetLibrary.HqBase}${name}.glb`);
    const root = gltf.scene;
    const caster = AssetLibrary.shadowCaster(name, root);
    root.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      mesh.castShadow = caster(mesh);
      mesh.receiveShadow = true;
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      const converted = mats.map((m) => this.hqMaterials.convert(m as THREE.MeshStandardMaterial, mesh, name));
      mesh.material = converted.length === 1 ? converted[0] : converted;
    });
    if (gltf.animations.length > 0) AssetLibrary.bakeRestPose(root, gltf.animations);
    const rigged = RigidRig.Families.test(name) && RigidRig.merge(root) > 0;
    SkinSlots.tag(root, name);                         // the skin kits' material slots per vertex (render/skins/kit/slots.ts)
    return { root, clips: gltf.animations, rigged };
  }

  /**
   * Fresh copies of shipped models for another WebGL context (the skins sheet's turntable, SkinPreview): loaded and
   * converted like the library's own but never registered, so they keep the CPU arrays that context uploads from
   * while the library's own textures are released after the main canvas uploads them (cpuRelease.ts). Build with
   * them inside `withCopies`; free them with `disposeCopies` once that context is done with them. A model that fails
   * to load is left out (the build then uses the library's own, if any).
   */
  async copies(names: Iterable<string>): Promise<Map<string, HqCopy>> {
    const out = new Map<string, HqCopy>();
    const todo = [...new Set(names)].filter((n) => this.shipped.has(n));
    await AssetLibrary.pool(todo, async (n) => {
      try { out.set(n, await this.prepareHq(n)); } catch (err) { console.warn(`hq asset ${n}: copy failed to load`, err); }
    });
    return out;
  }

  /** Runs `build` (synchronous) with `copies` standing in for the library's models of the same names. */
  withCopies<T>(copies: ReadonlyMap<string, HqCopy>, build: () => T): T {
    const saved: { key: string; root: THREE.Object3D | undefined; clips: THREE.AnimationClip[] | undefined; rigged: boolean; scale: number | undefined }[] = [];
    for (const [name, c] of copies) {
      const key = `hq:${name}`;
      saved.push({ key, root: this.models.get(key), clips: this.clips.get(key), rigged: this.rigged.has(key), scale: this.hqScale.get(key) });
      this.models.set(key, c.root);
      this.hqScale.set(key, 1);
      if (c.clips.length > 0) this.clips.set(key, c.clips); else this.clips.delete(key);
      if (c.rigged) this.rigged.add(key); else this.rigged.delete(key);
    }
    try { return build(); } finally {
      for (const s of saved) {
        if (s.root) this.models.set(s.key, s.root); else this.models.delete(s.key);
        if (s.clips) this.clips.set(s.key, s.clips); else this.clips.delete(s.key);
        if (s.rigged) this.rigged.add(s.key); else this.rigged.delete(s.key);
        if (s.scale !== undefined) this.hqScale.set(s.key, s.scale); else this.hqScale.delete(s.key);
      }
    }
  }

  /** Frees copies made by `copies` (geometry, materials, textures; nothing the library's own models use). */
  disposeCopies(copies: ReadonlyMap<string, HqCopy>): void {
    for (const c of copies.values()) AssetLibrary.disposeTree(c.root, this.hqMaterials);
  }


  /**
   * Unloads every HQ model not in `keep`: geometry, textures, materials (and the material module's references to
   * them) go back to the GPU. Call once the previous screen's views are disposed — clones share the source's GPU
   * resources. Returns the names released.
   */
  release(keep: ReadonlySet<string>): string[] {
    const gone: string[] = [];
    for (const name of this.loadedNames()) {
      if (keep.has(name)) continue;
      const key = `hq:${name}`;
      AssetLibrary.disposeTree(this.models.get(key)!, this.hqMaterials);
      this.models.delete(key); this.clips.delete(key); this.hqScale.delete(key); this.rigged.delete(key);
      this.hqCount--;
      gone.push(name);
    }
    return gone;
  }

  static readonly disposeTree = disposeTree;

  /**
   * The models a WebGL context other than the main canvas's draws from the library's own copy, so their textures
   * keep the CPU copy it uploads from (cpuRelease.ts): the end screen's chest stage (chests) and the tower skins'
   * kits (their pieces are cached once and dressed on the hub's towers and the turntable alike). The skins sheet's
   * turntable draws towers, the fortress and troops from private copies (`copies`). The debug capture renderers
   * (IconRender, SkinThumbs, the readability sheet) draw anything: their pages run with `?allhq` or `?sheet`, which
   * turn the release off (or pass `?keepcpu`).
   */
  static readonly SharedWithOtherContexts = /^(chest_|skins\/)/;

  /**
   * The KTX2 transcoder runs in two workers, each holding the Basis WebAssembly heap grown to the largest texture it
   * has transcoded, and three.js keeps them for the page's life. Once nothing has been transcoded for
   * `TranscoderIdleMs` (a level's or the hub's load done, the next level's preload in), they are terminated; the
   * next KTX2 load starts new ones (three's WorkerPool creates a worker on demand). Only an idle pool is
   * terminated: one with a job running or queued is left until it settles.
   */
  static readonly TranscoderIdleMs = 10_000;
  private static transcoderTimer: ReturnType<typeof setTimeout> | null = null;
  /** Workers terminated so far (diagnostics). */
  static transcoderRetired = 0;

  /** Hooks the KTX2 loader's worker pool so every transcode (re)starts the idle clock. */
  static watchTranscoder(loader: KTX2Loader): void {
    const pool = (loader as unknown as { workerPool?: TranscoderPool }).workerPool;
    if (!pool || typeof pool.postMessage !== "function") return;
    const post = pool.postMessage.bind(pool);
    pool.postMessage = (msg: unknown, transfer?: Transferable[]) => {
      AssetLibrary.transcoderBusy();
      return post(msg, transfer).finally(() => AssetLibrary.transcoderBusy());
    };
  }

  private static transcoderBusy(): void {
    if (AssetLibrary.transcoderTimer !== null) clearTimeout(AssetLibrary.transcoderTimer);
    AssetLibrary.transcoderTimer = setTimeout(() => { AssetLibrary.transcoderTimer = null; AssetLibrary.retireTranscoder(); }, AssetLibrary.TranscoderIdleMs);
  }

  /** Terminates the transcoder's workers if none is busy; true when it did. */
  static retireTranscoder(loader: KTX2Loader | null = AssetLibrary.compressed): boolean {
    const pool = (loader as unknown as { workerPool?: TranscoderPool } | null)?.workerPool;
    if (!pool || pool.workers.length === 0) return false;
    if (pool.workerStatus !== 0 || pool.queue.length > 0) { AssetLibrary.transcoderBusy(); return false; }
    pool.dispose();
    AssetLibrary.transcoderRetired++;
    return true;
  }

  /**
   * Loads with a few requests in flight rather than all of them: 180 simultaneous fetches through a tunnel or a phone
   * connection starve each other, and one stalled socket used to hold the loading bar forever. Each file gets a
   * deadline and one retry; a file that still fails is skipped and its views draw nothing for it.
   *
   * The deadline is for a download that STALLS (no byte for `StallMs`), not for a slow one: a flat 45 s cut a 4 MB
   * fortress off mid-download on 4G through the tunnel (a dozen files share the link), and the retry started it over
   * from the first byte. `TimeoutMs` still caps a file that trickles forever.
   */
  static readonly Concurrency = 6;
  static readonly StallMs = 30_000;
  static readonly TimeoutMs = 180_000;

  private loadGlb(url: string): Promise<{ scene: THREE.Group; animations: THREE.AnimationClip[] }> {
    return loadWithDeadline(this.loader, url, AssetLibrary.StallMs, AssetLibrary.TimeoutMs);
  }

  static pool<T>(items: readonly T[], work: (item: T) => Promise<void>, width = AssetLibrary.Concurrency): Promise<void> { return poolWork(items, work, width); }

  /** Which of a model's meshes cast a shadow (assetTrees.ts). */
  static readonly shadowCaster = shadowCaster;
  static readonly NoShadowParts = NO_SHADOW_PARTS;

  /** Material conversion for the HQ set (art/SHADING.md): PBR kept, `_glow` unlit, vertex wind on ground/props. */
  private readonly hqMaterials = new HqMaterials();
  /** Per-model metadata from `art/hq/anchors.json` (blockers, wind, clips). */
  anchors = new HqAnchors();
  /** Baked glTF clips per HQ model (`idle`, `walk`, `fire`, `build`, …); the rigs in `render/rig.ts` play them. */
  private readonly clips = new Map<string, THREE.AnimationClip[]>();
  clipsOf(name: string): readonly THREE.AnimationClip[] { return this.clips.get(name) ?? []; }

  /** Writes the last frame of `build` into the nodes as the rest pose (assetTrees.ts). */
  static readonly bakeRestPose = bakeRestPose;

  /** Models whose parts were merged into skinned meshes (rigidRig.ts): their clones must re-bind to their own nodes. */
  private readonly rigged = new Set<string>();

  /** True when the HQ set has this model: `hq("tower_cannon_t1")`. */
  hq(name: string): boolean {
    if (this.models.has(`hq:${name}`)) return true;
    // A screen asked for a shipped model its asset set (render/assetNeeds.ts) did not list: this use draws nothing,
    // and the model loads in the background for the next one. Logged so the set can be fixed. A model that already
    // failed is not fetched again from here (a view asking every frame would loop); RETRY goes through `ensure`.
    if (this.lazy && this.shipped.has(name) && !this.inflight.has(name) && !this.failed.has(name)) {
      if (!this.missed.has(name)) { this.missed.add(name); console.info(`hq: ${name} was not preloaded for this screen; loading it now`); }
      void this.ensure([name]);
    }
    return false;
  }

  has(name: string): boolean { return this.models.has(name); }

  /** Fresh instance scaled to grid units, pivot at the tile centre on the ground. */
  clone(name: string, tint?: number): THREE.Object3D {
    const src = this.models.get(name);
    // a model that is not in memory (not shipped, failed, or missed by the screen's set): an empty group, drawn as nothing
    if (!src) return new THREE.Group();
    const inst = this.rigged.has(name) ? RigidRig.clone(src) : src.clone(true);
    inst.scale.setScalar(this.hqScale.get(name) ?? 1);
    if (tint !== undefined) tintTree(inst, tint);
    return inst;
  }

  /** Height of a model in grid units (for stacking tower storeys). */
  height(name: string): number {
    const src = this.models.get(name);
    if (!src) return 0;
    const box = new THREE.Box3().setFromObject(src);
    return (box.max.y - box.min.y) * (this.hqScale.get(name) ?? 1);
  }

  toonMaterial(color: number): THREE.MeshToonMaterial {
    return new THREE.MeshToonMaterial({ color, gradientMap: this.gradient });
  }
}

/** The part of three's WorkerPool (examples/jsm/utils/WorkerPool.js) the transcoder's idle teardown uses. */
interface TranscoderPool {
  workers: Worker[];
  workerStatus: number;
  queue: unknown[];
  postMessage(msg: unknown, transfer?: Transferable[]): Promise<unknown>;
  dispose(): void;
}

/** A loaded, converted model: its scene, its clips, and whether its parts were merged into a rigid rig. */
export interface HqCopy { readonly root: THREE.Object3D; readonly clips: THREE.AnimationClip[]; readonly rigged: boolean }
